/**
 * Preparación de permisos al terminar el registro del teléfono (QA-3 de la 005; FR-002, FR-031).
 *
 * **Por qué existe.** La primera marcación tras instalar salía sin ubicación aunque el trabajador
 * concediera los permisos: en ese primer intento los diálogos de cámara y de ubicación se abrían a
 * la vez, el GPS estaba frío y el ciclo VPP (~1 s) terminaba antes de que llegara una lectura.
 * Pedir los dos permisos **antes**, en frío y con una explicación, deja la primera marcación igual
 * que las siguientes.
 *
 * **Qué NO hace.** No guarda ni envía coordenadas: la lectura de `getCurrentPosition` se
 * **descarta** en el mismo callback (FR-031: las coordenadas se capturan únicamente durante la
 * marcación). Tampoco guarda imágenes: el `MediaStream` de `getUserMedia` se detiene en cuanto
 * llega. Solo interesa que el navegador recuerde el permiso.
 *
 * Todo es inyectable para probarlo sin `getUserMedia`, `geolocation` ni Permissions API. Nada lanza:
 * cada salida es un estado.
 */

/** Resultado de pedir un permiso. `unavailable`: el navegador no lo soporta o falló por otra causa. */
export type PermissionResult = 'granted' | 'denied' | 'unavailable';

export interface MarkingPermissionsResult {
  camera: PermissionResult;
  location: PermissionResult;
}

type GetUserMediaLike = (constraints: MediaStreamConstraints) => Promise<MediaStream>;
type GeolocationLike = Pick<Geolocation, 'getCurrentPosition'>;
type PermissionsLike = Pick<Permissions, 'query'>;

export interface MarkingPermissionsDeps {
  /** Por defecto `navigator.mediaDevices.getUserMedia`. */
  getUserMedia?: GetUserMediaLike | null;
  /** Por defecto `navigator.geolocation`. */
  geolocation?: GeolocationLike | null;
  /** Por defecto `navigator.permissions`. */
  permissions?: PermissionsLike | null;
}

interface ResolvedDeps {
  getUserMedia: GetUserMediaLike | null;
  geolocation: GeolocationLike | null;
  permissions: PermissionsLike | null;
}

/**
 * Plazo de la lectura de ubicación de la preparación. Por especificación el `timeout` de
 * `getCurrentPosition` no cuenta la espera del diálogo de permiso, así que solo acota cuánto se
 * espera al GPS **después** de concederlo — y aquí ese resultado ni siquiera se usa.
 */
export const PREPARE_LOCATION_TIMEOUT_MS = 10_000;

function defaultGetUserMedia(): GetUserMediaLike | null {
  if (typeof navigator === 'undefined') {
    return null;
  }
  const devices = navigator.mediaDevices as MediaDevices | undefined;
  return devices !== undefined && typeof devices.getUserMedia === 'function'
    ? (constraints) => devices.getUserMedia(constraints)
    : null;
}

function defaultGeolocation(): GeolocationLike | null {
  if (typeof navigator === 'undefined') {
    return null;
  }
  const geolocation = navigator.geolocation as Geolocation | undefined;
  return geolocation !== undefined && typeof geolocation.getCurrentPosition === 'function'
    ? geolocation
    : null;
}

function defaultPermissions(): PermissionsLike | null {
  if (typeof navigator === 'undefined') {
    return null;
  }
  const permissions = (navigator as { permissions?: Permissions }).permissions;
  return permissions !== undefined && typeof permissions.query === 'function' ? permissions : null;
}

function resolveDeps(deps: MarkingPermissionsDeps): ResolvedDeps {
  return {
    getUserMedia: deps.getUserMedia === undefined ? defaultGetUserMedia() : deps.getUserMedia,
    geolocation: deps.geolocation === undefined ? defaultGeolocation() : deps.geolocation,
    permissions: deps.permissions === undefined ? defaultPermissions() : deps.permissions,
  };
}

async function queryState(
  permissions: PermissionsLike,
  name: string,
): Promise<PermissionState | null> {
  try {
    // `camera` no está en el `PermissionName` de lib.dom de todos los TS; el navegador que no lo
    // conoce rechaza (Firefox), y ese rechazo se traduce en "no se sabe".
    const status = await permissions.query({ name } as PermissionDescriptor);
    return status.state;
  } catch {
    return null;
  }
}

/**
 * `true` si la Permissions API confirma que cámara **y** ubicación ya están concedidas (entonces
 * la preparación no molesta). `false` en cualquier otro caso, incluido "no se puede saber": ahí se
 * ofrece la preparación, que el trabajador puede omitir.
 */
export async function markingPermissionsAlreadyGranted(
  deps: MarkingPermissionsDeps = {},
): Promise<boolean> {
  const { permissions } = resolveDeps(deps);
  if (permissions === null) {
    return false;
  }
  const [camera, location] = await Promise.all([
    queryState(permissions, 'camera'),
    queryState(permissions, 'geolocation'),
  ]);
  return camera === 'granted' && location === 'granted';
}

function cameraResultOf(error: unknown): PermissionResult {
  const name = typeof error === 'object' && error !== null ? (error as { name?: unknown }).name : undefined;
  return name === 'NotAllowedError' || name === 'SecurityError' ? 'denied' : 'unavailable';
}

async function requestCamera(
  getUserMedia: GetUserMediaLike,
): Promise<PermissionResult> {
  try {
    const stream = await getUserMedia({ video: { facingMode: 'environment' }, audio: false });
    // Solo interesa el permiso: la cámara se apaga en el acto, sin tocar ningún fotograma.
    for (const track of stream.getTracks()) {
      track.stop();
    }
    return 'granted';
  } catch (error) {
    return cameraResultOf(error);
  }
}

function requestLocation(
  geolocation: GeolocationLike,
): Promise<PermissionResult> {
  return new Promise((resolve) => {
    try {
      geolocation.getCurrentPosition(
        // FR-031: la posición se DESCARTA aquí mismo. No se guarda, no se envía, no se loguea.
        () => resolve('granted'),
        (error) => {
          // 1 = PERMISSION_DENIED. 2/3 (sin señal, plazo) implican que el permiso sí se concedió.
          resolve(error.code === 1 ? 'denied' : 'granted');
        },
        { enableHighAccuracy: false, timeout: PREPARE_LOCATION_TIMEOUT_MS, maximumAge: Infinity },
      );
    } catch {
      resolve('unavailable');
    }
  });
}

/**
 * Pide los dos permisos, **uno después del otro** (dos diálogos simultáneos confunden, sobre todo
 * en iPhone): primero la cámara, después la ubicación. Debe llamarse desde un gesto del usuario.
 */
export async function requestMarkingPermissions(
  deps: MarkingPermissionsDeps = {},
): Promise<MarkingPermissionsResult> {
  const { getUserMedia, geolocation } = resolveDeps(deps);
  const camera = getUserMedia === null ? 'unavailable' : await requestCamera(getUserMedia);
  const location = geolocation === null ? 'unavailable' : await requestLocation(geolocation);
  return { camera, location };
}
